import { Step, useComputed, useSnackbar } from "@homebound/beam";
import { fragment, useFormState } from "@homebound/form-state";
import kebabCase from "lodash/kebabCase";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { useNavigationCheck } from "src/components/navigation/NavigationContext";
import { StepperProvider, useStepperContext } from "src/components/stepper";
import { WizardStep } from "src/components/stepper/useStepperWizard/useStepperWizard";
import { ReadyPlanOptionGroupPersonalizationType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { StepNav } from "src/routes/libraries/design-catalog/personalization-option/components/StepNav";
import {
  EditPersonalizationOptionWizardQuery,
  useEditPersonalizationOptionWizard_CombinationsQuery,
  useEditPersonalizationOptionWizardQuery,
  useSavePersonalizationRpogWizardMutation,
} from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import { DetailsStep } from "src/routes/libraries/design-catalog/personalization-option/steps/DetailsStep";
import { VariantsStep } from "src/routes/libraries/design-catalog/personalization-option/steps/VariantsStep";
import {
  addExistingMaterials,
  buildPlaceholderColumns,
  buildRpoIdToRpogIdMap,
  formConfig,
  getCombinationsMissingProducts,
  getGroupByFeatureForSave,
  initializeOptions,
  mapToForm,
  packageOptionsProductsSnapshot,
} from "src/routes/libraries/design-catalog/personalization-option/utils";
import { IdOrAddParams } from "src/routes/routesDef";
import { createDesignPackageUrl, createEditPersonalizationOptionUrl } from "src/RouteUrls";
import { fail } from "src/utils";
import { queryResult } from "src/utils/queryResult";

/** Creates/updates Personalization Options via a multi-step stepper. */
export function EditPersonalizationOptionWizard() {
  const searchParams = new URLSearchParams(useLocation().search);
  const forTli = searchParams.get("forTli") || undefined;
  const returnTo = searchParams.get("returnTo") || undefined;
  const {
    idOrAdd: rpogId,
    designPackageId: dpId,
    designPackageVersionId: rpavId,
  } = useRouteParams<IdOrAddParams & { designPackageId: string; designPackageVersionId: string }>();
  const isAdd = rpogId === "add";

  const query = useEditPersonalizationOptionWizardQuery({
    variables: {
      dpId,
      rpavId,
      rpogId,
      isAdd, // We'll @skip querying for the rpogId when `isAdd` is true
      forTliId: forTli ?? "",
      hasTliId: !!forTli, // We use `forTli` in both new-option & add-to-option flows
    },
  });
  return queryResult(query, (data) => (
    // "Edit latest version" stays on this route with a new rpavId, so key on the loaded version to remount
    // the view's `onlyOnce` form with that version's data
    <EditPersonalizationOptionWizardView
      key={data.designPackage.version.id}
      data={data}
      dpId={dpId}
      rpavId={rpavId}
      rpogId={rpogId}
      forTli={forTli}
      returnTo={returnTo}
    />
  ));
}

type EditPersonalizationOptionWizardViewProps = {
  data: EditPersonalizationOptionWizardQuery;
  dpId: string;
  rpavId: string;
  rpogId: string;
  forTli: string | undefined;
  returnTo: string | undefined;
};

function EditPersonalizationOptionWizardView(props: EditPersonalizationOptionWizardViewProps) {
  const { data, dpId, rpavId, rpogId, forTli, returnTo } = props;
  const isAdd = rpogId === "add";
  const { triggerNotice } = useSnackbar();
  const navigate = useNavigate();
  const [saveRpogWizard] = useSavePersonalizationRpogWizardMutation();
  const formState = useFormState({
    config: formConfig,
    // The form owns the data after the first map. Re-mapping on later cache writes (e.g. product search returning
    // a product whose image changed since we loaded) makes form-state merge the server's cells back in next to the
    // edited ones, b/c product cells have no id to match on, and the duplicate cells fail the save's uniqueness check.
    init: { input: data, map: (data) => mapToForm(data, isAdd, forTli), onlyOnce: true },
    readOnly: data.designPackage.canEdit.allowed === false,
  });
  const { useRegisterNavigationCheck } = useNavigationCheck();
  useRegisterNavigationCheck(() => !formState.dirty, [formState]);
  const personalizationType = useComputed(() => formState.type.value, [formState]);
  const outdatedVersion = useMemo(
    () =>
      data.designPackage.aggregateLatest.id !== rpavId
        ? {
            viewingVersionLabel: data.designPackage.version.displayVersion,
            // Keep the user on this same option, just jump to it on the latest version.
            editLatestVersionUrl: createEditPersonalizationOptionUrl(
              dpId,
              data.designPackage.aggregateLatest.id,
              rpogId,
              forTli,
              returnTo,
            ),
          }
        : undefined,
    [data, rpavId, dpId, rpogId, forTli, returnTo],
  );
  const saveDisabled = useComputed(
    () =>
      // More user friendly save disabled message when viewing an older version.
      (outdatedVersion && "Only the latest version of a design package can be edited.") ||
      disableBasedOnPotentialOperation(data.designPackage.canEdit) ||
      !formState.dirty ||
      (!formState.valid && (formState.errors.toString() || true)),
    [formState, data, outdatedVersion],
  );

  useEffect(() => {
    const tli = data.takeoffLineItem;
    if (isAdd && personalizationType && tli) {
      // When adding, use the initial placeholder's products as the initial materials
      // (Before careful inside this impl to not nuke wip data, as the useEffect can easily be called twice)
      initializeOptions(personalizationType, formState, tli);
    } else if (!isAdd && tli) {
      // When add-to-option-ing, fill in the existing cards
      const rpoIdToRpogId = buildRpoIdToRpogIdMap(data.designPackage.availableTargetGroups);
      addExistingMaterials(formState, tli, rpoIdToRpogId);
      // This `useEffect` can be called twice, so only add if it doesn't exist yet
      if (!formState.placeholders.value.some((p) => p.id === tli.id)) {
        formState.placeholders.set([...formState.placeholders.value, { id: tli.id, data: fragment(tli) }]);
      }
    }
  }, [formState, personalizationType, isAdd, data]);

  async function save() {
    const { placeholders, productOptions, packageOptions, ...rest } = formState.changedValue;
    // Get groupByFeature flag + normalized products from form state.
    // Note: groupByFeature used to be a manually set flag. v2 derives it instead
    const locationIdsByMaterial = formState.placeholders.rows
      .groupByObject((p) => p.data.value.materialVariant?.id)
      .reduce<Record<string, string[]>>((acc, [mv, slots]) => {
        if (mv) acc[mv] = slots.map((s) => s.data.value.location.id).unique();
        return acc;
      }, {});
    const groupBySave = getGroupByFeatureForSave(formState.packageOptions.value, locationIdsByMaterial);
    const { data } = await saveRpogWizard({
      variables: {
        input: {
          ...(isAdd ? { designPackageId: dpId } : {}),
          ...rest,
          // This is exhaustive; any placeholder IDs that we leave out will be unhooked
          controlledSlots: formState.placeholders.value.map((tli) => ({
            placeholderId: tli.id,
            order: tli.order ?? null,
          })),
          ...(formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package
            ? packageOptions
              ? {
                  groupByFeature: groupBySave.groupByFeature,
                  // This is exhaustive; any package options that we leave out will be unhooked
                  packageOptions: groupBySave.packageOptions.map((po) => ({
                    ...po,
                    products: po.products?.map(({ productMaterial, ...p }) => ({
                      placeholderMaterialId: p.placeholderMaterialId,
                      productMaterialId: productMaterial?.id ?? null,
                      locationId: p.locationId,
                    })),
                  })),
                }
              : {}
            : {
                productOptions: formState.productOptions.value?.map((po) => ({
                  materialVariantId: po.materialVariantId,
                  // Single defaults live on the regular slot's product cards
                  personalizationTargetCombinations:
                    formState.type.value === ReadyPlanOptionGroupPersonalizationType.SingleProduct
                      ? []
                      : po.personalizationTargetCombinations?.map((combo) => ({ options: combo.options })),
                  curationTargetCombinations: po.curationTargetCombinations?.map((combo) => ({
                    options: combo.options,
                  })),
                  checkoutOrder: po.checkoutOrder,
                })),
              }),
        },
      },
      refetchQueries: ["DesignPackageItemSlotEditor"],
    });
    if (!data) return;
    // Use the version ID from the mutation response, which may be a new draft if the save
    // created one (e.g. when editing an active version with no existing draft).
    const newRpavId = data.savePersonalizationOptionGroup.version.id;
    triggerNotice({ message: `Option ${isAdd ? "created" : "updated"} successfully`, icon: "success" });
    // Marks the just-saved values as the new clean baseline so the nav-check below doesn't block
    // the navigate() we're about to do ourselves.
    formState.commitChanges();
    // The wizard is a flow, so exit once saved: return to where the user came from, or fall back to
    // the design package page (on the version the save landed on, which may be a freshly-created draft).
    navigate(returnTo ?? createDesignPackageUrl(dpId, newRpavId), { replace: true });
  }

  const maybeRpog = data.readyPlan?.optionGroups.first;
  const selectedTargetGroups = useComputed(
    () => formState.personalizationTargetGroupIds.value ?? [],
    [formState.personalizationTargetGroupIds],
  );
  // Get option combinations based on the selected target groups
  const combinationsQuery = useEditPersonalizationOptionWizard_CombinationsQuery({
    variables: { dpId, rpavId, targetGroupIds: selectedTargetGroups },
    skip: selectedTargetGroups.isEmpty,
  });

  const cancelUrl = returnTo || createDesignPackageUrl(dpId, rpavId);
  const targetGroups = data.designPackage.availableTargetGroups;
  const targetCombinations = useMemo(
    () => combinationsQuery.data?.designPackage.combinations ?? [],
    [combinationsQuery.data],
  );
  const isPackage = personalizationType === ReadyPlanOptionGroupPersonalizationType.Package;
  const combinationsMissingProducts = useComputed(() => {
    if (!isPackage) return [];
    const packageOptionsSnapshot = packageOptionsProductsSnapshot(formState);
    return getCombinationsMissingProducts(
      packageOptionsSnapshot,
      targetCombinations,
      buildPlaceholderColumns([...formState.placeholders.rows], packageOptionsSnapshot),
    );
  }, [formState, targetCombinations, isPackage]);
  const title = maybeRpog ? `Edit ${maybeRpog.name} Option` : "Create New Option";

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        // Step 1 bundles the option details, the target groups it applies to, and its slots.
        label: "Details",
        render: () => (
          <DetailsStep
            formState={formState}
            title={title}
            personalizationTypes={data.enumDetails.readyPlanOptionGroupPersonalizationType}
            isExisting={!!maybeRpog}
            targetGroups={targetGroups}
            combinationsMissingProducts={combinationsMissingProducts}
            outdatedVersion={outdatedVersion}
          />
        ),
      },
      {
        label: "Variants",
        render: () => (
          <VariantsStep
            formState={formState}
            title={title}
            targetGroups={targetGroups}
            targetCombinations={targetCombinations}
          />
        ),
      },
    ],
    [formState, data, title, targetGroups, targetCombinations, combinationsMissingProducts, outdatedVersion, maybeRpog],
  );

  const initialSteps = useMemo<Step[]>(
    () => steps.map((step) => ({ ...step, state: "incomplete", value: kebabCase(step.label) })),
    [steps],
  );

  return (
    <StepperProvider steps={initialSteps} newLayout>
      <StepPicker steps={steps} />
      <StepNav cancelUrl={cancelUrl} saveDisabled={saveDisabled} onSave={save} saveLabel={isAdd ? "Create" : "Save"} />
    </StepperProvider>
  );
}

function StepPicker({ steps }: { steps: WizardStep[] }) {
  const { currentStep } = useStepperContext();
  const { render: Component } =
    steps.find((s) => s.label === currentStep.label) ?? fail(`No stepper step found for ${currentStep.label}`);
  return <>{Component()}</>;
}
